import {useLocale} from "@/contexts/localeContext.tsx";
import {useDialog} from "@/components/dialog/DialogContext.tsx";
import React, {FC, useState} from "react";
import ActivityFrame from "@/components/activity-frame/activity-frame.tsx";
import {TableContextProvider} from "@/contexts/table/TableContextProvider.tsx";
import IdentityProviderGroupColunms from "@/models/ColumnDefinitions/IdentityProviderGroupColunms.ts";
import {DEF_PG_SIZE_SUB_MIN} from "@/constants/maximum-page-size.ts";
import {ListData} from "@/models/ListData.tsx";
import {ListQuery} from "@/models/ListQuery.ts";
import TableTab from "@/components/tabs/table-tab.tsx";
import {TranslationKeys} from "@/contexts/TranslationKeys.ts";
import {appendFilter} from "@/utilities/filters.ts";
import {Lookup} from "@/models/Lookup.ts";
import {DeleteEntityResult} from "@/models/services/DeleteEntityResult.ts";
import {deleteIdentityProviderGroupMap, getIdentityProviderGroupsByAccountId, getIdentityProviderGroupsById} from "@/services/mobileAccountService.ts";
import MapGroupWizard from "@/components/map-groups/MapGroupWizard.tsx";
import {getErrorMessage} from "@/services/utilities/ServiceError.ts";
import {IdentityProviderGroup} from "@/models/IdentityProviderGroup.ts";
import identityProviderGroupSearch from "@/models/search/IdentityProviderGroupSearch.ts";
import ManageButton from "../assignWizard/ManageButton";

interface AuthAccountAdvancedProps {
    isAccountPopulated: boolean,
    currentItem: any
}

export const AuthAccountAdvanced: FC<AuthAccountAdvancedProps> = ({isAccountPopulated, currentItem}) => {
    const {translate} = useLocale();
    const dialog = useDialog();
    const [busy, setBusy] = useState(false);
    const [lookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [showCreateMappingWizard, setShowCreateMappingWizard] = useState(false);
    const initialMapping: IdentityProviderGroup = {
        id: 0,
        idpGroupName: "",
        identityProviderGroupId: "",
        idpGroupDescription: "",
        profileName: "",
        profileId: 0,
        accountName: currentItem.accountName,
        accountId: currentItem.id,
    }
    const [currentMapping, setCurrentMapping] = useState<IdentityProviderGroup>(initialMapping)

    const closeMappingWizard = () => {
        setShowCreateMappingWizard(false);
        setCurrentMapping(initialMapping)
    }

    const onDelete = async (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteIdentityProviderGroupMap(id).then(deleteResult => {
                    resolve(deleteResult);
                    setBusy(false);
                },
                deleteResult => {
                    reject(deleteResult);
                    setBusy(false);
                })
        });
    }

    const getIdentityProviderGroups = (listParameters: any) => {
        return getIdentityProviderGroupsByAccountId(translate, {
            ...listParameters,
            filters: appendFilter(currentItem.id, 'Account', 'account.id', listParameters?.filters)
        });
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => {
        return
    });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const editMapGroups = async (id: string) => {
        try {
            const groupMap = await getIdentityProviderGroupsById(id);
            setCurrentMapping(groupMap)
            setShowCreateMappingWizard(true);
        } catch (error) {
            dialog.pushError({title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error)});
        }
    }

    return <ActivityFrame className='flex flex-col flex-grow bg-white' busy={busy}>
        <TableContextProvider
            dataMethod={(listParameters) => getIdentityProviderGroups(listParameters)}
            tableDescriptor={IdentityProviderGroupColunms}
            searchDescriptor={identityProviderGroupSearch}
            usePagination
            useFilter
            useSearch
            useSort
            useTable
            lookup={lookup}
            manualDataRefresh={manualDataRefresh}
            deleteMethod={onDelete}
            editMethod={editMapGroups}
            maxPageSize={DEF_PG_SIZE_SUB_MIN}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedIdentityProviderGroups)}
                setShowModal={setShowCreateMappingWizard}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                selectRows={false}
                cyName='groupMapping'>
                <ManageButton onClick={() => setShowCreateMappingWizard(true)}/>
            </TableTab>
        </TableContextProvider>
        <MapGroupWizard
            onClose={() => closeMappingWizard()}
            isOpen={showCreateMappingWizard}
            account={currentItem}
            setBusy={setBusy}
            currentGroup={currentMapping}
            setSelectedGroup={setCurrentMapping}/>
    </ActivityFrame>
};
